import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BrandLogo } from '../components/BrandLogo';
import { PageMeta } from '../components/PageMeta';
import { PhoneMockup } from '../components/PhoneMockup';
import { StoreBadges } from '../components/StoreBadges';
import { useLandingScreens } from '../hooks/useLandingScreens';

type FeatureItem = {
  label: string;
  title: string;
  body: string;
};

const FEATURE_SCREENS = ['discover', 'book', 'bookings'] as const;

function featureSectionClass(index: number): string {
  if (index === 1) {
    return 'section-band section-band--mid-light feature-section';
  }
  return 'section section-flat feature-section';
}

export function HomePage() {
  const { t } = useTranslation();
  const { screen } = useLandingScreens();
  const features = t('features', { returnObjects: true }) as FeatureItem[];
  const hero = screen('hero');

  return (
    <>
      <PageMeta titleKey="homePage.metaTitle" descriptionKey="homePage.metaDescription" />

      <section className="section-band section-band--dark-mid">
        <div className="hero hero--branded">
          <div className="hero-copy">
            <div className="hero-logo-row">
              <BrandLogo variant="combo" className="hero-logo-combo" alt="Shmatch" />
            </div>
            <div className="hero-text">
              <h1 className="hero-headline">{t('hero.eyebrow')}</h1>
              <p className="hero-tagline">{t('hero.title')}</p>
              <p className="hero-sub">{t('hero.subtitle')}</p>
            </div>
            <div className="hero-ctas">
              <StoreBadges />
            </div>
            <p className="hero-vendors">
              <Link to="/vendors">{t('hero.ctaVendors')} →</Link>
            </p>
          </div>
          <div className="hero-visual">
            <PhoneMockup
              label={t('phoneMockup.screenshot')}
              imageSrc={hero.src}
              imageAlt={hero.alt}
            />
          </div>
        </div>
      </section>

      {features.map((feature, index) => {
        const shot = screen(FEATURE_SCREENS[index]);
        return (
          <section key={feature.title} className={featureSectionClass(index)}>
            <div className="section-inner">
              <div className={`feature-row${index % 2 === 1 ? ' reverse' : ''}`}>
                <div className="feature-copy">
                  <div className="feature-label">{feature.label}</div>
                  <h2>{feature.title}</h2>
                  <p>{feature.body}</p>
                </div>
                <div className="feature-visual">
                  <PhoneMockup
                    label={
                      shot.src ? shot.alt : t('phoneMockup.placeholderDiscover')
                    }
                    imageSrc={shot.src}
                    imageAlt={shot.alt}
                  />
                </div>
              </div>
            </div>
          </section>
        );
      })}
    </>
  );
}
